import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ShieldCheck, UserCog } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import AuthContext from '@/contexts/AuthContext';
import { assistApi, BRANDS, OVERALL, STAGE_STATUS } from './assistApi';
import { Stage2Form, Stage3Form, Stage4Form } from './AssistFormsA';
import { Stage5Form, Stage6Form, Stage7Form } from './AssistFormsB';
import { ApproveNowDialog } from './ApproveNowDialog';

const STAGES = [
  { n: 2, key: 'stage2_profile', title: 'Company profile & coverage', Form: Stage2Form },
  { n: 3, key: 'stage3_online', title: 'Online presence', Form: Stage3Form },
  { n: 4, key: 'stage4_insurance', title: 'Insurance certificate', Form: Stage4Form },
  { n: 5, key: 'stage5_first_install', title: 'First live installation', Form: Stage5Form },
  { n: 6, key: 'stage6_documentation', title: 'Project documentation (photos)', Form: Stage6Form },
  { n: 7, key: 'stage7_review', title: 'Customer review', Form: Stage7Form },
];

function StageRow({ stage, record, open, onToggle, children }) {
  const data = record[stage.key] || {};
  const st = STAGE_STATUS[data.status] || STAGE_STATUS.not_started;
  return (
    <div className="rounded-xl border border-gray-200 bg-white" data-testid={`assist-stage-${stage.n}`}>
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid={`assist-stage-${stage.n}-toggle`}>
        <span className="w-6 h-6 rounded-full bg-[#27292E] text-white text-xs font-bold inline-flex items-center justify-center shrink-0">{stage.n}</span>
        <span className="font-semibold text-sm text-[#27292E] flex-1">{stage.title}</span>
        {data.entered_by_staff && <span className="text-[10px] text-gray-500 hidden sm:inline">entered by {data.entered_by_staff.name}</span>}
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`assist-stage-${stage.n}-status`}>{st.label}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 pb-4 border-t border-gray-100 pt-3">{children}</div>}
    </div>
  );
}

export function ApprovalAssistDrawer({ open, onClose, userId, brand: initialBrand = 'probound', installer, onChanged }) {
  const { user } = useContext(AuthContext);
  const canApprove = ['admin', 'manager'].includes(user?.role);
  const [brand, setBrand] = useState(initialBrand);
  const [record, setRecord] = useState(null);
  const [openStage, setOpenStage] = useState(null);
  const [notify, setNotify] = useState(true);
  const [approveOpen, setApproveOpen] = useState(false);

  useEffect(() => { if (open) setBrand(initialBrand); }, [open, initialBrand]);
  useEffect(() => {
    if (!open || !userId) return;
    setRecord(null);
    assistApi.get(userId, brand).then(setRecord).catch((e) => toast.error(e.message));
  }, [open, userId, brand]);

  const update = (d) => { setRecord(d); onChanged?.(d); };
  const overall = OVERALL[record?.overall_status] || OVERALL.not_started;
  const brandMeta = BRANDS.find((b) => b.id === brand);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="approval-assist-drawer">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-gray-100 text-left">
          <SheetTitle className="flex items-center gap-2 text-base"><UserCog className="w-5 h-5 text-[#7A0B7A]" /> Ox Approved — complete on behalf of {installer?.company_name || installer?.name || 'installer'}</SheetTitle>
          <div className="text-xs text-gray-500">{installer?.email}</div>
        </SheetHeader>

        <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-b border-gray-100 bg-gray-50">
          {BRANDS.map((b) => (
            <button key={b.id} type="button" onClick={() => setBrand(b.id)} data-testid={`assist-brand-${b.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${brand === b.id ? 'bg-[#27292E] text-white border-[#27292E]' : 'bg-white text-gray-700 border-gray-300'}`}>{b.label}</button>
          ))}
          <span className={`ml-auto text-xs font-semibold px-2.5 py-1 rounded-full border ${overall.cls}`} data-testid="assist-overall-status">{overall.label}</span>
        </div>

        <div className="px-5 py-3 flex flex-wrap items-center gap-3 text-xs">
          <label className="flex items-center gap-1.5 cursor-pointer text-gray-700">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="accent-[#7A0B7A]" data-testid="assist-notify" />
            Email the installer when I save a stage / approve
          </label>
          {canApprove && record && record.overall_status !== 'approved' && (
            <Button size="sm" onClick={() => setApproveOpen(true)} className="ml-auto bg-emerald-600 hover:bg-emerald-700" data-testid="assist-approve-now">
              <ShieldCheck className="w-4 h-4 mr-1.5" /> Approve now
            </Button>
          )}
          {record?.overall_status === 'approved' && (
            <span className="ml-auto text-emerald-700 font-semibold" data-testid="assist-approved-label">
              Approved{record.approved_directly_by ? ` by ${record.approved_directly_by.name}` : ''}{record.approved_directly_by?.skipped_stages?.length ? ` · skipped stages ${record.approved_directly_by.skipped_stages.join(', ')}` : ''}
            </span>
          )}
        </div>

        <div className="px-5 pb-6 space-y-2">
          {!record && <div className="text-sm text-gray-500 py-6 text-center">Loading…</div>}
          {record && STAGES.map((s) => (
            <StageRow key={s.n} stage={s} record={record} open={openStage === s.n} onToggle={() => setOpenStage(openStage === s.n ? null : s.n)}>
              <s.Form userId={userId} brand={brand} data={record[s.key] || {}} notify={notify} onSaved={update} />
            </StageRow>
          ))}
        </div>

        {record && <ApproveNowDialog open={approveOpen} onClose={() => setApproveOpen(false)} record={record} brandLabel={brandMeta?.label} notify={notify} onApproved={update} />}
      </SheetContent>
    </Sheet>
  );
}
